import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import Header from "@/components/shared/Header";
import Waveform from "@/components/shared/Waveform";
import TranscriptViewer from "@/components/shared/TranscriptViewer";
import DocumentViewer from "@/components/shared/DocumentViewer";
import KeyMomentsList from "@/components/shared/KeyMomentsList";
import TranscriptChat from "@/components/shared/TranscriptChat";
import { VideoReviewCard } from "@/components/shared/VideoReviewCard";
import { SpeakerManagementDialog } from "@/components/shared/SpeakerManagementDialog";
import { SegmentSpeakerDialog } from "@/components/shared/SegmentSpeakerDialog";
import { SpeakerTimelineEditor } from "@/components/shared/SpeakerTimelineEditor";
import { TranscriptSegmentEditorDialog } from "@/components/shared/TranscriptSegmentEditorDialog";
import { TranscriptBulkEditorDialog } from "@/components/shared/TranscriptBulkEditorDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import videoApi from "@/lib/api";
import { getApiErrorMessage } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { formatDuration, formatDate, getStatusLabel, getProcessingMessage } from "@/lib/utils";
import {
  Loader2,
  ArrowLeft,
  Download,
  Trash2,
  Clock,
  Calendar,
  RefreshCw,
  AlertTriangle,
  Sparkles,
  FileText,
  MessageSquareText,
  Mic2,
  PencilLine,
} from "lucide-react";

const normalizeEvidenceText = (value = "") => value.toLowerCase().replace(/\s+/g, " ").trim();

const getCandidateEvidenceFragments = (value = "") => {
  const rawCandidates = value
    .split(/\n+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const unique = Array.from(new Set(rawCandidates));
  return unique.sort((a, b) => b.length - a.length).slice(0, 10);
};

const sharedTokenScore = (left = "", right = "") => {
  const leftTokens = new Set(normalizeEvidenceText(left).split(" ").filter((token) => token.length >= 5));
  const rightTokens = new Set(normalizeEvidenceText(right).split(" ").filter((token) => token.length >= 5));
  let score = 0;
  leftTokens.forEach((token) => {
    if (rightTokens.has(token)) {
      score += 1;
    }
  });
  return score;
};

const parseChatTimestampLabelToSeconds = (value = "") => {
  if (!value || typeof value !== "string") return null;
  const firstToken = value.split(/[–-]/)[0]?.trim();
  if (!firstToken) return null;
  const parts = firstToken.split(":").map((part) => Number(part));
  if (parts.some((part) => Number.isNaN(part))) return null;
  if (parts.length === 2) {
    const [minutes, seconds] = parts;
    return minutes * 60 + seconds;
  }
  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;
    return hours * 3600 + minutes * 60 + seconds;
  }
  return null;
};

const resolveDocumentEvidenceTarget = (evidence = {}, documentBlocks = []) => {
  if (!Array.isArray(documentBlocks) || documentBlocks.length === 0) {
    return null;
  }

  const candidateFragments = getCandidateEvidenceFragments(evidence?.excerpt || evidence?.text || "");
  const candidateBlockIds = Array.isArray(evidence?.block_ids)
    ? evidence.block_ids.filter(Boolean)
    : (evidence?.block_id ? [evidence.block_id] : []);
  const pageStart = Number.isFinite(evidence?.page_start) ? evidence.page_start : (Number.isFinite(evidence?.page_number) ? evidence.page_number : null);
  const pageEnd = Number.isFinite(evidence?.page_end) ? evidence.page_end : pageStart;

  const scopedBlocks = documentBlocks.filter((block) => {
    if (candidateBlockIds.length > 0) {
      return candidateBlockIds.includes(block.id);
    }
    if (pageStart !== null && Number.isFinite(block?.page_number)) {
      return block.page_number >= pageStart && block.page_number <= (pageEnd ?? pageStart);
    }
    return true;
  });

  const blocksToEvaluate = scopedBlocks.length > 0 ? scopedBlocks : documentBlocks;
  let bestMatch = null;

  blocksToEvaluate.forEach((block) => {
    let score = 0;
    if (evidence?.block_id && block.id === evidence.block_id) {
      score += 120;
    }
    if (candidateBlockIds.includes(block.id)) {
      score += 80;
    }
    if (pageStart !== null && Number.isFinite(block?.page_number) && block.page_number >= pageStart && block.page_number <= (pageEnd ?? pageStart)) {
      score += 40;
    }

    let chosenExcerpt = "";
    let excerptStart = null;
    let excerptEnd = null;

    candidateFragments.forEach((fragment) => {
      if (!fragment || chosenExcerpt) return;
      const exactIndex = block.text.indexOf(fragment);
      if (exactIndex >= 0) {
        chosenExcerpt = fragment;
        excerptStart = exactIndex;
        excerptEnd = exactIndex + fragment.length;
        score += 160 + Math.min(fragment.length, 140) / 10;
      }
    });

    if (!chosenExcerpt && candidateFragments.length > 0) {
      const normalizedBlock = normalizeEvidenceText(block.text || "");
      const fuzzyFragment = candidateFragments.find((fragment) => normalizedBlock.includes(normalizeEvidenceText(fragment)));
      if (fuzzyFragment) {
        chosenExcerpt = fuzzyFragment;
        score += 70 + Math.min(fuzzyFragment.length, 120) / 12;
      }
    }

    score += sharedTokenScore(block.text || "", evidence?.text || evidence?.excerpt || "");

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = {
        blockId: block.id,
        excerpt: chosenExcerpt || candidateFragments[0] || "",
        excerptStart,
        excerptEnd,
        score,
      };
    }
  });

  return bestMatch;
};

const VideoDetailPage = () => {
  const isTerminalVideoStatus = useCallback((status) => ["completed", "failed", "cancelled"].includes(status), []);

  const hasNamedSpeakerProfiles = useCallback((profiles = []) => (
    (profiles || []).some((profile) => String(profile?.custom_name || "").trim() || String(profile?.suggested_name || "").trim())
  ), []);

  const mergeVideoProgressSafely = useCallback((previousVideo, incomingVideo) => {
    if (!previousVideo || !incomingVideo) return incomingVideo;
    if (isTerminalVideoStatus(previousVideo.status) || isTerminalVideoStatus(incomingVideo.status)) {
      return incomingVideo;
    }

    const previousProgress = Number(previousVideo.progress || 0);
    const incomingProgress = Number(incomingVideo.progress || 0);
    if (incomingProgress >= previousProgress) {
      return incomingVideo;
    }

    const shouldPreserveNamedProfiles = hasNamedSpeakerProfiles(previousVideo.speaker_profiles) && !hasNamedSpeakerProfiles(incomingVideo.speaker_profiles);

    return {
      ...incomingVideo,
      progress: previousProgress,
      processing_message: previousVideo.processing_message || incomingVideo.processing_message,
      speaker_profiles: shouldPreserveNamedProfiles
        ? (previousVideo.speaker_profiles || incomingVideo.speaker_profiles || [])
        : incomingVideo.speaker_profiles,
      speaker_analysis_progress:
        incomingVideo.speaker_analysis_status === "processing"
          ? Math.max(Number(previousVideo.speaker_analysis_progress || 0), Number(incomingVideo.speaker_analysis_progress || 0))
          : incomingVideo.speaker_analysis_progress,
      speaker_analysis_message:
        incomingVideo.speaker_analysis_status === "processing"
          ? (incomingVideo.speaker_analysis_message || previousVideo.speaker_analysis_message)
          : incomingVideo.speaker_analysis_message,
    };
  }, [hasNamedSpeakerProfiles, isTerminalVideoStatus]);

  const { language } = useLanguage();
  const { user } = useAuth();
  const { videoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [highlightedSegment, setHighlightedSegment] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [translatingMoments, setTranslatingMoments] = useState(false);
  const [translatingTranscript, setTranslatingTranscript] = useState(false);
  const [displayTranscript, setDisplayTranscript] = useState("");
  const [displaySegments, setDisplaySegments] = useState([]);
  const [displayDocumentBlocks, setDisplayDocumentBlocks] = useState([]);
  const [highlightedDocumentBlockId, setHighlightedDocumentBlockId] = useState(null);
  const [highlightedExcerpt, setHighlightedExcerpt] = useState("");
  const [highlightedExcerptStart, setHighlightedExcerptStart] = useState(null);
  const [highlightedExcerptEnd, setHighlightedExcerptEnd] = useState(null);
  const [speakerDialogOpen, setSpeakerDialogOpen] = useState(false);
  const [savingSpeakerId, setSavingSpeakerId] = useState("");
  const [mergingSpeakerId, setMergingSpeakerId] = useState("");
  const [deletingSpeakerId, setDeletingSpeakerId] = useState("");
  const [identifyingSpeakers, setIdentifyingSpeakers] = useState(false);
  const [speakerProgressVisible, setSpeakerProgressVisible] = useState(false);
  const [speakerProgressValue, setSpeakerProgressValue] = useState(0);
  const [speakerProgressMessage, setSpeakerProgressMessage] = useState("");
  const [speakerProgressRunId, setSpeakerProgressRunId] = useState("");
  const [segmentSpeakerDialogOpen, setSegmentSpeakerDialogOpen] = useState(false);
  const [selectedSpeakerSegment, setSelectedSpeakerSegment] = useState(null);
  const [updatingSegmentSpeaker, setUpdatingSegmentSpeaker] = useState(false);
  const [segmentTranscriptDialogOpen, setSegmentTranscriptDialogOpen] = useState(false);
  const [selectedTranscriptSegment, setSelectedTranscriptSegment] = useState(null);
  const [bulkTranscriptDialogOpen, setBulkTranscriptDialogOpen] = useState(false);
  const [savingTranscript, setSavingTranscript] = useState(false);
  const transcriptRef = useRef(null);

  const inferContentLanguage = useCallback(
    (...candidates) => {
      const match = candidates.find((value) => value === "es" || value === "en");
      return match || language;
    },
    [language]
  );

  const handleMomentClick = (momentOrTime) => {
    if (video?.content_kind === "document") {
      const target = typeof momentOrTime === "object" && momentOrTime !== null
        ? resolveDocumentEvidenceTarget(momentOrTime, displayDocumentBlocks)
        : null;

      if (target?.blockId) {
        setHighlightedDocumentBlockId(target.blockId);
        setHighlightedExcerpt(target.excerpt || (typeof momentOrTime?.excerpt === "string" ? momentOrTime.excerpt : ""));
        setHighlightedExcerptStart(Number.isInteger(target.excerptStart) ? target.excerptStart : (Number.isInteger(momentOrTime?.excerpt_start) ? momentOrTime.excerpt_start : null));
        setHighlightedExcerptEnd(Number.isInteger(target.excerptEnd) ? target.excerptEnd : (Number.isInteger(momentOrTime?.excerpt_end) ? momentOrTime.excerpt_end : null));
        setHighlightedSegment(typeof momentOrTime?.segment_index === "number" ? momentOrTime.segment_index : null);
        if (transcriptRef.current?.scrollToBlock) {
          transcriptRef.current.scrollToBlock(target.blockId);
        }
      } else {
        setHighlightedDocumentBlockId(null);
        setHighlightedExcerpt("");
        setHighlightedExcerptStart(null);
        setHighlightedExcerptEnd(null);
      }
      return;
    }

    if (video?.segments) {
      const explicitSegmentIndex = typeof momentOrTime === "object" && momentOrTime !== null
        ? momentOrTime.segment_index
        : null;
      const targetTime = typeof momentOrTime === "object" && momentOrTime !== null
        ? parseFloat(momentOrTime.time_seconds ?? 0)
        : parseFloat(momentOrTime);

      if (explicitSegmentIndex !== null && explicitSegmentIndex !== undefined) {
        setHighlightedSegment(explicitSegmentIndex);
        setHighlightedExcerpt(typeof momentOrTime?.excerpt === "string" ? momentOrTime.excerpt : "");
        if (transcriptRef.current) {
          transcriptRef.current.scrollToSegment(explicitSegmentIndex);
        }
        return;
      }

      let closestIdx = 0;
      let closestDiff = Infinity;
      
      video.segments.forEach((seg, idx) => {
        const diff = Math.abs(parseFloat(seg.start) - targetTime);
        if (diff < closestDiff) {
          closestDiff = diff;
          closestIdx = idx;
        }
      });
      
      setHighlightedSegment(closestIdx);
      setHighlightedDocumentBlockId(null);
      setHighlightedExcerpt("");
      setHighlightedExcerptStart(null);
      setHighlightedExcerptEnd(null);
      
      // Scroll to segment
      if (transcriptRef.current) {
        transcriptRef.current.scrollToSegment(closestIdx);
      }
    }
  };

  const handleDocumentCitationClick = useCallback((evidence) => {
    if (!evidence || video?.content_kind !== "document") {
      return;
    }

    const transcriptTab = document.querySelector('[data-testid="mobile-tab-transcript"]');
    if (transcriptTab && typeof window !== "undefined" && window.innerWidth < 1024) {
      transcriptTab.click();
    }

    const target = resolveDocumentEvidenceTarget(evidence, displayDocumentBlocks);
    if (!target?.blockId) {
      return;
    }

    setHighlightedDocumentBlockId(target.blockId);
    setHighlightedExcerpt(target.excerpt || "");
    setHighlightedExcerptStart(Number.isInteger(target.excerptStart) ? target.excerptStart : null);
    setHighlightedExcerptEnd(Number.isInteger(target.excerptEnd) ? target.excerptEnd : null);
    setHighlightedSegment(null);

    window.setTimeout(() => {
      if (transcriptRef.current?.scrollToBlock) {
        transcriptRef.current.scrollToBlock(target.blockId);
      }
    }, transcriptTab ? 180 : 40);
  }, [displayDocumentBlocks, video?.content_kind]);

  const handleChatTimestampClick = useCallback((timestampLabel) => {
    if (!timestampLabel || video?.content_kind === "document") {
      return;
    }

    const targetSeconds = parseChatTimestampLabelToSeconds(timestampLabel);
    if (!Number.isFinite(targetSeconds)) {
      return;
    }

    const transcriptTab = document.querySelector('[data-testid="mobile-tab-transcript"]');
    if (transcriptTab && typeof window !== "undefined" && window.innerWidth < 1024) {
      transcriptTab.click();
      window.setTimeout(() => handleMomentClick(targetSeconds), 180);
      return;
    }

    handleMomentClick(targetSeconds);
  }, [handleMomentClick, video?.content_kind]);

  const handleSummarize = async () => {
    if (summarizing) return;
    const analysisLanguage = inferContentLanguage(video?.summary_language, video?.transcript_language, video?.language);
    
    setSummarizing(true);
    try {
      const result = await videoApi.summarize(videoId, analysisLanguage);
      setSummary(result.summary);
      setVideo((prev) => prev ? {
        ...prev,
        summary: result.summary,
        summary_language: result.language || analysisLanguage,
      } : prev);
      if (result.cached) {
        toast.info(language === "es" ? "Resumen cargado" : "Loaded existing summary");
      } else {
        toast.success(language === "es" ? "Resumen generado exitosamente" : "Summary generated successfully");
      }
    } catch (error) {
      console.error("Failed to generate summary:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "Error al generar el resumen" : "Failed to generate summary", language));
    } finally {
      setSummarizing(false);
    }
  };

  const fetchVideo = useCallback(async () => {
    try {
      const data = await videoApi.get(videoId);
      setVideo((prev) => mergeVideoProgressSafely(prev, data));
      setDisplayTranscript(data.transcript || "");
      setDisplaySegments(data.segments || []);
      // Load existing summary if available
      if (data.summary) {
        setSummary(data.summary);
      }
    } catch (error) {
      console.error("Failed to fetch video:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos cargar el detalle del video" : "Failed to load video details", language));
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [language, mergeVideoProgressSafely, navigate, videoId]);

  const handleRenameSpeaker = useCallback(async (speakerId, customName) => {
    try {
      setSavingSpeakerId(speakerId);
      const result = await videoApi.renameSpeaker(videoId, speakerId, { custom_name: customName || "" });
      setDisplaySegments(result.segments || []);
      setVideo((prev) => prev ? {
        ...prev,
        segments: result.segments || prev.segments,
        speaker_profiles: result.speaker_profiles || prev.speaker_profiles || [],
        speaker_insights: result.speaker_insights || prev.speaker_insights || [],
      } : prev);
      toast.success(language === "es" ? "Nombre del speaker actualizado" : "Speaker name updated");
    } catch (error) {
      console.error("Failed to rename speaker:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos guardar el speaker" : "We couldn't save the speaker", language));
    } finally {
      setSavingSpeakerId("");
    }
  }, [language, videoId]);

  const handleMergeSpeakers = useCallback(async (fromSpeakerId, intoSpeakerId) => {
    try {
      setMergingSpeakerId(fromSpeakerId);
      const result = await videoApi.mergeSpeakers(videoId, {
        from_speaker_id: fromSpeakerId,
        into_speaker_id: intoSpeakerId,
      });
      setDisplaySegments(result.segments || []);
      setVideo((prev) => prev ? {
        ...prev,
        segments: result.segments || prev.segments,
        speaker_profiles: hasNamedSpeakerProfiles(result.speaker_profiles)
          ? (result.speaker_profiles || [])
          : (prev.speaker_profiles || result.speaker_profiles || []),
        speaker_insights: result.speaker_insights || prev.speaker_insights || [],
      } : prev);
      await fetchVideo();
      toast.success(language === "es" ? "Speakers fusionados" : "Speakers merged");
    } catch (error) {
      console.error("Failed to merge speakers:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos fusionar speakers" : "We couldn't merge speakers", language));
    } finally {
      setMergingSpeakerId("");
    }
  }, [fetchVideo, hasNamedSpeakerProfiles, language, videoId]);

  const handleDeleteSpeaker = useCallback(async (speakerId) => {
    try {
      setDeletingSpeakerId(speakerId);
      const result = await videoApi.deleteSpeaker(videoId, speakerId);
      setDisplaySegments(result.segments || []);
      setVideo((prev) => prev ? {
        ...prev,
        segments: result.segments || prev.segments,
        speaker_profiles: result.speaker_profiles || prev.speaker_profiles || [],
        speaker_insights: result.speaker_insights || prev.speaker_insights || [],
      } : prev);
      toast.success(language === "es" ? "Speaker borrado" : "Speaker deleted");
    } catch (error) {
      console.error("Failed to delete speaker:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos borrar el speaker" : "We couldn't delete the speaker", language));
    } finally {
      setDeletingSpeakerId("");
    }
  }, [language, videoId]);

  const handleUpdateSegmentSpeaker = useCallback(async (selection) => {
    if (!selectedSpeakerSegment) return;
    try {
      setUpdatingSegmentSpeaker(true);
      const result = await videoApi.updateSegmentSpeaker(videoId, selectedSpeakerSegment.index, {
        speaker_id: selection === "__new__" ? null : selection,
        create_new: selection === "__new__",
      });
      setDisplaySegments(result.segments || []);
      setVideo((prev) => prev ? {
        ...prev,
        segments: result.segments || prev.segments,
        speaker_profiles: result.speaker_profiles || prev.speaker_profiles || [],
        speaker_insights: result.speaker_insights || prev.speaker_insights || [],
      } : prev);
      setSegmentSpeakerDialogOpen(false);
      setSelectedSpeakerSegment(null);
      toast.success(language === "es" ? "Segmento reasignado" : "Segment reassigned");
    } catch (error) {
      console.error("Failed to update segment speaker:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos reasignar el segmento" : "We couldn't reassign the segment", language));
    } finally {
      setUpdatingSegmentSpeaker(false);
    }
  }, [language, selectedSpeakerSegment, videoId]);

  const handleUpdateSpeakerRange = useCallback(async (payload) => {
    try {
      setUpdatingSegmentSpeaker(true);
      const result = await videoApi.updateSpeakerRange(videoId, payload);
      setDisplaySegments(result.segments || []);
      setVideo((prev) => prev ? {
        ...prev,
        segments: result.segments || prev.segments,
        speaker_profiles: result.speaker_profiles || prev.speaker_profiles || [],
        speaker_insights: result.speaker_insights || prev.speaker_insights || [],
      } : prev);
      toast.success(language === "es" ? "Rango de speaker actualizado" : "Speaker range updated");
    } catch (error) {
      console.error("Failed to update speaker range:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos actualizar el rango del speaker" : "We couldn't update the speaker range", language));
    } finally {
      setUpdatingSegmentSpeaker(false);
    }
  }, [language, videoId]);

  const handleIdentifySpeakers = useCallback(async () => {
    let shouldKeepProgressVisible = false;
    const fallbackMessage = language === "es"
      ? "Leyendo el juicio completo para entender quién habla y cuándo vuelve a intervenir."
      : "Reading the full hearing to understand who is speaking and when they return.";

    try {
      setIdentifyingSpeakers(true);
      setSpeakerProgressVisible(true);
      setSpeakerProgressValue(3);
      setSpeakerProgressMessage(video?.speaker_analysis_message || fallbackMessage);
      setSpeakerProgressRunId("");

      const result = await videoApi.analyzeSpeakers(videoId, { force: true });
      setDisplaySegments(result.segments || []);
      setVideo((prev) => prev ? {
        ...prev,
        segments: result.segments || prev.segments,
        speaker_profiles: result.speaker_profiles || prev.speaker_profiles || [],
        speaker_insights: result.speaker_insights || prev.speaker_insights || [],
        speaker_analysis_status: result.analysis_status || prev.speaker_analysis_status || null,
        speaker_analysis_message: result.message || null,
        speaker_analysis_error: result.error || null,
        speaker_analysis_progress: typeof result.analysis_progress === "number"
          ? result.analysis_progress
          : prev.speaker_analysis_progress || 0,
      } : prev);

      setSpeakerProgressValue(result.analysis_status === "processing" ? Math.max(3, Number(result.analysis_progress || 0)) : 100);
      setSpeakerProgressMessage(result.message || fallbackMessage);

      if (result.analysis_status === "processing") {
        shouldKeepProgressVisible = true;
        setSpeakerProgressVisible(true);
        toast.info(language === "es"
          ? "La identificación de speakers quedó corriendo en segundo plano. La página se actualizará sola cuando termine."
          : "Speaker identification is running in the background. This page will refresh automatically when it finishes.");
        await fetchVideo();
        return;
      }

      setSpeakerProgressValue(100);
      toast.success(language === "es" ? "Speakers identificados con IA" : "Speakers identified with AI");
    } catch (error) {
      console.error("Failed to identify speakers:", error);
      setSpeakerProgressVisible(false);
      setSpeakerProgressValue(0);
      setSpeakerProgressMessage("");
      setSpeakerProgressRunId("");
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos identificar speakers" : "We couldn't identify speakers", language));
    } finally {
      setIdentifyingSpeakers(false);
      if (!shouldKeepProgressVisible) {
        setSpeakerProgressVisible(false);
      }
    }
  }, [fetchVideo, language, video?.speaker_analysis_message, video?.speaker_analysis_progress, videoId]);

  const applyTranscriptUpdate = useCallback((result) => {
    setDisplayTranscript(result.transcript || "");
    setDisplaySegments(result.segments || []);
    setVideo((prev) => prev ? {
      ...prev,
      transcript: result.transcript || prev.transcript,
      segments: result.segments || prev.segments,
    } : prev);
  }, []);

  const buildEditableSegmentPayload = useCallback((segments) => (
    (segments || [])
      .filter((segment) => !segment?.is_system_marker)
      .map((segment) => ({
        start: Number(segment.start || 0),
        end: Number(segment.end || 0),
        text: segment.text || "",
      }))
  ), []);

  const handleSaveTranscriptSegment = useCallback(async (payload) => {
    if (!selectedTranscriptSegment) return;
    try {
      setSavingTranscript(true);
      const nextSegments = (displaySegments || []).map((segment, index) => (
        index === selectedTranscriptSegment.index
          ? {
              ...segment,
              start: payload.start,
              end: payload.end,
              text: payload.text,
            }
          : segment
      ));
      const result = await videoApi.updateTranscript(videoId, {
        mode: "segments",
        segments: buildEditableSegmentPayload(nextSegments),
      });
      applyTranscriptUpdate(result);
      await fetchVideo();
      setSegmentTranscriptDialogOpen(false);
      setSelectedTranscriptSegment(null);
      toast.success(language === "es" ? "Fragmento actualizado" : "Segment updated");
    } catch (error) {
      console.error("Failed to update transcript segment:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos guardar el fragmento" : "We couldn't save the segment", language));
    } finally {
      setSavingTranscript(false);
    }
  }, [applyTranscriptUpdate, buildEditableSegmentPayload, displaySegments, fetchVideo, language, selectedTranscriptSegment, videoId]);

  const handleSaveBulkTranscript = useCallback(async (transcriptValue) => {
    try {
      setSavingTranscript(true);
      const result = await videoApi.updateTranscript(videoId, {
        mode: "full_text",
        transcript: transcriptValue,
      });
      applyTranscriptUpdate(result);
      await fetchVideo();
      setBulkTranscriptDialogOpen(false);
      toast.success(language === "es" ? "Transcripción actualizada" : "Transcript updated");
    } catch (error) {
      console.error("Failed to update full transcript:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos guardar la transcripción" : "We couldn't save the transcript", language));
    } finally {
      setSavingTranscript(false);
    }
  }, [applyTranscriptUpdate, fetchVideo, language, videoId]);

  useEffect(() => {
    fetchVideo();
  }, [fetchVideo]);

  useEffect(() => {
    setTranslatingMoments(false);
    setTranslatingTranscript(false);
    setHighlightedDocumentBlockId(null);
    setHighlightedExcerpt("");
    setHighlightedExcerptStart(null);
    setHighlightedExcerptEnd(null);
    if (!video) return;
    setDisplayTranscript(video.transcript || "");
    setDisplaySegments(video.segments || []);
    setDisplayDocumentBlocks(video.document_blocks || []);
  }, [video]);

  useEffect(() => {
    if (!video) {
      return;
    }

    const startedAt = String(video.speaker_analysis_started_at || "").trim();

    if (video.speaker_analysis_status === "processing") {
      if (startedAt && startedAt !== speakerProgressRunId) {
        setSpeakerProgressRunId(startedAt);
        setSpeakerProgressValue(Math.max(3, Number(video.speaker_analysis_progress || 0)));
      } else {
        setSpeakerProgressValue(Number(video.speaker_analysis_progress || 0));
      }
      setSpeakerProgressVisible(true);
      setSpeakerProgressMessage((current) => video.speaker_analysis_message || current || (language === "es"
        ? "Leyendo el juicio completo para entender quién habla y cuándo vuelve a intervenir."
        : "Reading the full hearing to understand who is speaking and when they return."));
      return;
    }

    if (video.speaker_analysis_status === "completed") {
      setSpeakerProgressRunId("");
      setSpeakerProgressValue(100);
      setSpeakerProgressVisible(false);
      return;
    }

    if (video.speaker_analysis_status === "failed") {
      setSpeakerProgressRunId("");
      setSpeakerProgressVisible(false);
      setSpeakerProgressValue(0);
      return;
    }
  }, [language, speakerProgressRunId, video]);

  useEffect(() => {
    const shouldAnimateProgress = identifyingSpeakers && video?.speaker_analysis_status !== "processing" && speakerProgressVisible;
    if (!shouldAnimateProgress) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setSpeakerProgressValue((current) => {
        const baseProgress = Math.max(current, 3);
        if (identifyingSpeakers) {
          if (baseProgress >= 8) {
            return baseProgress;
          }
          return Math.min(8, baseProgress + 1);
        }
        return baseProgress;
      });
    }, 1200);

    return () => window.clearInterval(interval);
  }, [identifyingSpeakers, speakerProgressVisible, video?.speaker_analysis_status]);

  // Auto-refresh for processing videos
  useEffect(() => {
    if (
      video &&
      (video.status === "pending" ||
        video.status === "processing" ||
        video.status === "extracting_audio" ||
        video.status === "transcribing" ||
        video.status === "analyzing" ||
        video.speaker_analysis_status === "processing")
    ) {
      const interval = setInterval(() => {
        fetchVideo();
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [video, fetchVideo]);

  const handleExport = async (format) => {
    try {
      const data = await videoApi.export(videoId, format, language);
      
      let blob;
      if (data.encoding === "base64") {
        // Handle binary files (PDF, DOCX)
        const binaryString = atob(data.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: data.mimetype });
      } else {
        // Handle text files (TXT, SRT)
        blob = new Blob([data.content], { type: "text/plain;charset=utf-8" });
      }
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(language === "es" ? `Exportado como ${format.toUpperCase()}` : `Exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "Error al exportar" : "Failed to export transcript", language));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this video?")) {
      return;
    }

    setDeleting(true);
    try {
      await videoApi.delete(videoId);
      toast.success("Video deleted successfully");
      navigate("/dashboard");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(getApiErrorMessage(error, language === "es" ? "No pudimos eliminar el video" : "Failed to delete video", language));
    } finally {
      setDeleting(false);
    }
  };

  const speakerProfiles = useMemo(() => {
    if (!video) {
      return [];
    }

    if ((video.speaker_profiles || []).length > 0) {
      return video.speaker_profiles || [];
    }

    const segmentSource = video.segments || displaySegments || [];
    const speakerStats = new Map();
    segmentSource.forEach((segment) => {
      if (!segment?.speaker_id || segment.speaker_id === "unknown") return;
      const existing = speakerStats.get(segment.speaker_id) || {
        speaker_id: segment.speaker_id,
        label: segment.speaker_label || segment.speaker_id,
        custom_name: null,
        suggested_name: null,
        segment_count: 0,
        total_talk_time_seconds: 0,
        sample_quote: "",
      };
      existing.segment_count += 1;
      existing.total_talk_time_seconds += Math.max(0, Number(segment.end || 0) - Number(segment.start || 0));
      if (!existing.sample_quote && segment.text) {
        existing.sample_quote = segment.text.slice(0, 220);
      }
      speakerStats.set(segment.speaker_id, existing);
    });
    return Array.from(speakerStats.values());
  }, [displaySegments, video]);

  const resolvedBackTarget = useMemo(() => "/dashboard", []);

  const goToDashboard = useCallback(() => {
    try {
      sessionStorage.setItem("voxara_last_detail_back_target", "/dashboard");
    } catch (_error) {
      // ignore session storage errors
    }

    if (typeof window !== "undefined") {
      window.location.assign("/dashboard");
      return;
    }

    navigate("/dashboard", { replace: true });
  }, [navigate]);

  useEffect(() => {
    try {
      sessionStorage.setItem("voxara_last_detail_back_target", resolvedBackTarget);
    } catch (_error) {
      // ignore session storage errors
    }
  }, [resolvedBackTarget]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const historyState = window.history.state || {};
    window.history.replaceState({
      ...historyState,
      voxaraVideoDetailGuard: true,
      voxaraVideoDetailBackTarget: resolvedBackTarget,
    }, "", window.location.href);

    window.history.pushState({
      ...historyState,
      voxaraVideoDetailSentinel: true,
    }, "", window.location.href);

    const handlePopState = (event) => {
      if (event.state?.voxaraVideoDetailGuard || event.state?.voxaraVideoDetailSentinel) {
        goToDashboard();
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [goToDashboard, resolvedBackTarget]);

  const handleBackToDashboard = useCallback(() => {
    goToDashboard();
  }, [goToDashboard]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-6 pt-24 pb-12 max-w-6xl">
          <Card className="bg-card border-border p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </Card>
        </main>
      </div>
    );
  }

  if (!video) {
    return null;
  }

  const isDocument = video.content_kind === "document";
  const isSpeakerFeatureEnabled = true;
  const isManualSpeakerEditorEnabled = isSpeakerFeatureEnabled && !isDocument && video.status === "completed";
  const hasDocumentPdfPreview = Boolean(isDocument && video.document_preview_url);
  const isPdfDocument = Boolean(isDocument && hasDocumentPdfPreview);
  const speakerInsights = video.speaker_insights || [];
  const visibleSpeakerProfiles = isSpeakerFeatureEnabled ? speakerProfiles : [];
  const sanitizedDisplaySegments = isSpeakerFeatureEnabled
    ? displaySegments
    : (displaySegments || []).map(({ speaker_id, speaker_label, ...segment }) => segment);
  const sanitizedKeyMoments = isSpeakerFeatureEnabled
    ? video.key_moments
    : (video.key_moments || []).map(({ speaker_id, speaker_quote, ...moment }) => moment);
  const isTranscriptEditable = !isDocument && video.status === "completed" && user?.id === video.user_id;
  const editableTranscriptText = (sanitizedDisplaySegments || [])
    .filter((segment) => !segment?.is_system_marker)
    .map((segment) => segment.text || "")
    .filter(Boolean)
    .join("\n\n");
  const transcriptAccuracyNotice = language === "es"
    ? "Voxara alcanza una precisión aproximada del 98,7% en la transcripción. Aun así, la calidad del audio, los silencios, el ruido de fondo, el solapamiento de voces o una pronunciación difícil pueden afectar el resultado. La identificación de speakers está en Beta y todavía puede confundir menciones con la persona que realmente habla, por lo que te recomendamos revisarla y editarla manualmente cuando haga falta."
    : "Voxara reaches an approximate 98.7% transcription accuracy. Even so, audio quality, silences, background noise, overlapping voices, or difficult pronunciation can affect the result. Speaker identification is currently in Beta and can still confuse mentions with the person who is actually speaking, so we recommend reviewing and editing it manually when needed.";
  const showSpeakerControls = isSpeakerFeatureEnabled && !isDocument && video.status === "completed" && speakerProfiles.length > 0;
  const isSpeakerAnalysisRunning = identifyingSpeakers || speakerProgressVisible || video.speaker_analysis_status === "processing";
  const speakerAnalysisProgress = isSpeakerAnalysisRunning
    ? Math.max(2, Math.min(100, video.speaker_analysis_status === "processing"
      ? Number(video.speaker_analysis_progress || 0)
      : Number(speakerProgressValue || 0)))
    : Math.max(0, Math.min(100, Number(video.speaker_analysis_progress || 0)));
  const speakerAnalysisMessage = speakerProgressMessage || video.speaker_analysis_message || (language === "es"
    ? "Leyendo el juicio completo para entender quién habla y cuándo vuelve a intervenir."
    : "Reading the full hearing to understand who is speaking and when they return.");
  const speakerButtonLabel = isSpeakerAnalysisRunning
    ? (language === "es" ? "Identificando speakers..." : "Identifying speakers...")
    : (language === "es" ? "Editar speakers (Beta)" : "Edit speakers (Beta)");
  const speakerHelperMessage = video.speaker_analysis_status === "processing"
    ? (language === "es"
      ? "La identificación de speakers está corriendo en segundo plano para evitar errores 502 en archivos largos. Esta vista se actualizará sola al terminar."
      : "Speaker identification is running in the background to avoid 502 errors on long files. This view will refresh automatically when it finishes.")
    : (language === "es"
      ? "Puedes editar los nombres de speakers antes de generar la presentación en PDF o Word. Esta función sigue en Beta."
      : "You can edit speaker names before generating the PDF or Word presentation. This feature is still in Beta.");
  const desktopPanelHeightClass = isDocument ? "min-h-[760px] xl:min-h-[820px]" : "min-h-[540px]";
  const desktopPanelBodyHeightClass = isDocument ? "h-[690px] xl:h-[748px]" : "h-[472px]";

  const isProcessing =
    video.status === "pending" ||
    video.status === "processing" ||
    video.status === "extracting_audio" ||
    video.status === "transcribing" ||
    video.status === "analyzing";

  return (
    <div className="min-h-screen bg-background notranslate" data-testid="video-detail-page" lang={language} translate="no">
      <Header />

      <main className="mx-auto w-full px-4 sm:px-6 lg:px-10 xl:px-12 2xl:px-16 pt-28 sm:pt-24 pb-12 max-w-[1920px]" translate="no">
        {/* Header - Responsive */}
        <div className={`flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 sm:mb-8 ${video.status === "completed" ? "lg:hidden" : ""}`}>
          <div className="min-w-0 flex-1">
            <a
              href="/dashboard"
              className="relative z-[70] flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-3 sm:mb-4"
              data-testid="mobile-back-to-dashboard-link"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === "es" ? "Volver al Dashboard" : "Back to Dashboard"}
            </a>
            <h1 className="text-lg sm:text-2xl font-bold tracking-tight break-words">
              {video.original_filename}
            </h1>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-muted-foreground">
              {video.duration_seconds && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                  {formatDuration(video.duration_seconds)}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                {formatDate(video.created_at, language)}
              </span>
              <Badge
                variant="outline"
                className={`status-${video.status} font-mono text-xs`}
              >
                {getStatusLabel(video.status, language)}
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {isTranscriptEditable && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkTranscriptDialogOpen(true)}
                className="text-xs sm:text-sm"
                data-testid="open-transcript-editor-button"
              >
                <PencilLine className="w-4 h-4 sm:mr-2" />
                <span>{language === "es" ? "Editar" : "Edit"}</span>
                <span className="hidden sm:inline sm:ml-1">{language === "es" ? "transcripción" : "transcript"}</span>
              </Button>
            )}
            {video.status === "completed" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="text-xs sm:text-sm" data-testid="export-btn">
                    <Download className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">{language === "es" ? "Exportar" : "Export"}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {!isDocument && (
                    <DropdownMenuItem onClick={() => handleExport("txt")} data-testid="export-txt">
                      <FileText className="w-4 h-4 mr-2" />
                      {language === "es" ? "Texto (.txt)" : "Text (.txt)"}
                    </DropdownMenuItem>
                  )}
                  {!isDocument && (
                    <DropdownMenuItem onClick={() => handleExport("srt")} data-testid="export-srt">
                      <FileText className="w-4 h-4 mr-2" />
                      {language === "es" ? "Subtítulos (.srt)" : "Subtitles (.srt)"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="export-pdf">
                    <FileText className="w-4 h-4 mr-2" />
                    PDF (.pdf)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport("docx")} data-testid="export-docx">
                    <FileText className="w-4 h-4 mr-2" />
                    Word (.docx)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={deleting}
              className="text-destructive hover:text-destructive"
              data-testid="delete-btn"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Processing State */}
        {isProcessing && (
          <Card className="bg-card border-border p-8 mb-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {getStatusLabel(video.status, language)}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {getProcessingMessage(video.status, language)}
              </p>
              {video.processing_message && (
                <p className="text-sm text-primary mb-4" data-testid="video-processing-message">
                  {video.processing_message}
                </p>
              )}
              
              {/* Progress Percentage */}
              <div className="mb-6">
                <div className="text-4xl font-bold text-primary mb-2">
                  {video.progress || 0}%
                </div>
                <div className="w-full max-w-xs mx-auto h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500 ease-out"
                    style={{ width: `${video.progress || 0}%` }}
                  />
                </div>
              </div>
              
              <Waveform isAnimating={true} barCount={60} />
            </div>
          </Card>
        )}

        {/* Failed State */}
        {video.status === "failed" && (
          <Card className="bg-card border-destructive/50 p-8 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-destructive mb-2">
                  Processing Failed
                </h3>
                <p className="text-muted-foreground text-sm">
                  {video.error_message || "An error occurred while processing your video."}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Completed State - Floating Draggable Panels (Desktop) */}
        {video.status === "completed" && (
          <>
            <div
              className="hidden lg:grid lg:gap-8 xl:gap-10 lg:items-start mb-6"
              style={{
                marginLeft: 'calc(50% - 50vw)',
                width: 'calc(100vw - 24px)',
                gridTemplateColumns: 'clamp(340px, 24vw, 460px) minmax(0, 1fr)',
              }}
            >
              <div />
              <div className="flex w-full items-start justify-between gap-8">
                <div className="min-w-0 flex-1">
                  <a
                    href="/dashboard"
                    className="relative z-[70] flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
                    data-testid="desktop-back-to-dashboard-link"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {language === "es" ? "Volver al Dashboard" : "Back to Dashboard"}
                  </a>
                  <h1 className="text-2xl font-bold tracking-tight break-words">
                    {video.original_filename}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                    {video.duration_seconds && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDuration(video.duration_seconds)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(video.created_at, language)}
                    </span>
                    <Badge variant="outline" className={`status-${video.status} font-mono text-xs`}>
                      {getStatusLabel(video.status, language)}
                    </Badge>
                  </div>
                  {showSpeakerControls ? (
                    <p className="mt-3 text-xs leading-5 text-muted-foreground" data-testid="desktop-speaker-helper-message">
                      {speakerHelperMessage}
                    </p>
                  ) : null}
                </div>

                <div className="ml-auto flex items-center gap-2 flex-shrink-0 self-start">
                  {isTranscriptEditable && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBulkTranscriptDialogOpen(true)}
                      className="text-sm"
                      data-testid="desktop-open-transcript-editor-button"
                    >
                      <PencilLine className="w-4 h-4 mr-2" />
                      <span>{language === "es" ? "Editar transcripción" : "Edit transcript"}</span>
                    </Button>
                  )}
                  {video.status === "completed" && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="text-sm" data-testid="desktop-export-btn">
                          <Download className="w-4 h-4 mr-2" />
                          <span>{language === "es" ? "Exportar" : "Export"}</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {!isDocument && (
                          <DropdownMenuItem onClick={() => handleExport("txt")} data-testid="desktop-export-txt">
                            <FileText className="w-4 h-4 mr-2" />
                            {language === "es" ? "Texto (.txt)" : "Text (.txt)"}
                          </DropdownMenuItem>
                        )}
                        {!isDocument && (
                          <DropdownMenuItem onClick={() => handleExport("srt")} data-testid="desktop-export-srt">
                            <FileText className="w-4 h-4 mr-2" />
                            {language === "es" ? "Subtítulos (.srt)" : "Subtitles (.srt)"}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleExport("pdf")} data-testid="desktop-export-pdf">
                          <FileText className="w-4 h-4 mr-2" />
                          PDF (.pdf)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport("docx")} data-testid="desktop-export-docx">
                          <FileText className="w-4 h-4 mr-2" />
                          Word (.docx)
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="text-destructive hover:text-destructive"
                    data-testid="desktop-delete-btn"
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {video.is_truncated_by_plan && (
              <Card className="bg-amber-500/10 border-amber-500/30 p-5 mb-6" data-testid="free-tier-truncation-banner">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-lg font-semibold text-foreground">
                      {language === "es" ? "Tu transcripción gratis llegó al límite de 30 minutos" : "Your free transcription reached the 30-minute limit"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {video.upgrade_message || (language === "es"
                        ? "El plan free solo procesa 1 video o audio de hasta 30 minutos en toda la vida de la cuenta. Haz upgrade para desbloquear el archivo completo y nuevos uploads."
                        : "The free tier only processes 1 lifetime video or audio up to 30 minutes. Upgrade to unlock the full file and new uploads.")}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => navigate('/pricing')} data-testid="free-tier-upgrade-pricing-button">
                      {language === "es" ? "Ver planes" : "View plans"}
                    </Button>
                    <Button onClick={() => navigate('/billing?plan=starter&provider=stripe')} data-testid="free-tier-upgrade-billing-button">
                      {language === "es" ? "Hacer upgrade" : "Upgrade now"}
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            <div
              className="hidden lg:grid lg:items-start lg:gap-6 xl:gap-8 min-[1800px]:xl:gap-10 lg:grid-cols-[clamp(340px,_24vw,_460px)_minmax(0,_1fr)]"
              style={{
                marginLeft: 'calc(50% - 50vw)',
                width: 'calc(100vw - 24px)',
              }}
              data-testid="video-detail-desktop-main-grid"
            >
              <Card className="bg-card border-border sticky top-24 h-[calc(100vh-120px)] overflow-hidden lg:rounded-l-none lg:border-l-0">
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="flex items-center gap-2 font-medium text-base lg:text-lg">
                        <Sparkles className="w-4 h-4 text-primary" />
                        {language === "es" ? "Momentos Clave" : "Key Moments"}
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground lg:text-[0.95rem]">
                        {language === "es"
                          ? (isPdfDocument ? "Lista lateral para saltar directamente a la página del PDF donde aparece cada momento clave" : (isDocument ? "Lista lateral para ubicar rápidamente las partes clave del texto" : "Lista lateral con scroll para navegar rápidamente por el video"))
                          : (isPdfDocument ? "Sidebar to jump directly to the PDF page for each key moment" : (isDocument ? "Scrollable sidebar to jump across the most important text fragments" : "Scrollable sidebar for fast navigation through the video"))}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Badge variant="outline" className="text-xs" data-testid="key-moments-count-badge">
                        {video.key_moments?.length || 0}
                      </Badge>
                      {showSpeakerControls ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setSpeakerDialogOpen(true)}
                          className="speaker-edit-cta"
                          data-testid="desktop-speaker-action-button"
                        >
                          <Mic2 className="mr-2 h-4 w-4" />
                          {speakerButtonLabel}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="h-[calc(100%-88px)] overflow-y-auto">
                  {translatingMoments ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {language === "es" ? "Traduciendo momentos..." : "Translating moments..."}
                        </p>
                      </div>
                      {showSpeakerControls ? (
                        <p className="mt-2 text-xs leading-5 text-muted-foreground" data-testid="mobile-speaker-helper-message">
                          {speakerHelperMessage}
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <KeyMomentsList
                      moments={sanitizedKeyMoments}
                      onMomentClick={handleMomentClick}
                      sidebarMode={true}
                      isDocument={isDocument}
                      speakerInsights={speakerInsights}
                      speakerProfiles={visibleSpeakerProfiles}
                      language={language}
                    />
                  )}
                </div>
              </Card>

              <div className="space-y-6 lg:space-y-8" data-testid="video-detail-reading-stack">
                <Card className={`bg-card border-border overflow-hidden ${isDocument ? desktopPanelHeightClass : 'min-h-[680px] xl:min-h-[760px]'}`}>
                  <div className="p-4 border-b border-border">
                    <h3 className="flex items-center gap-2 font-medium text-base lg:text-lg">
                      <MessageSquareText className="w-4 h-4 text-primary" />
                      {isDocument
                        ? (hasDocumentPdfPreview ? (language === "es" ? "PDF original" : "Original PDF") : (language === "es" ? "Documento de texto" : "Text document"))
                        : (language === "es" ? "Transcripción Completa" : "Full Transcript")}
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground lg:text-[0.95rem]">
                      {isDocument
                        ? (hasDocumentPdfPreview
                          ? (language === "es" ? "Vista fiel del PDF, más amplia y con salto directo por páginas desde Momentos Clave" : "Accurate PDF view with direct page jumps from Key Moments")
                          : (language === "es" ? "Texto ordenado del documento" : "Structured document text"))
                        : `${displaySegments?.length || video.segments?.length || 0} ${language === "es" ? "fragmentos" : "fragments"}`}
                    </p>
                  </div>
                  {!isDocument ? (
                    <div
                      className="mx-4 mb-4 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-xs leading-6 text-muted-foreground"
                      data-testid="desktop-transcript-accuracy-notice"
                    >
                      {transcriptAccuracyNotice}
                    </div>
                  ) : null}
                  <div className={`${isDocument ? desktopPanelBodyHeightClass : 'h-[610px] xl:h-[690px]'} overflow-y-auto`}>
                    {translatingTranscript ? (
                      <div className="flex items-center justify-center h-full" data-testid="desktop-transcript-translation-loading">
                        <div className="text-center">
                          <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">
                            {language === "es" ? "Traduciendo transcripción..." : "Translating transcript..."}
                          </p>
                        </div>
                      </div>
                    ) : (
                      isDocument && displayDocumentBlocks.length > 0 ? (
                        <DocumentViewer
                          ref={transcriptRef}
                          documentBlocks={displayDocumentBlocks}
                          highlightedBlockId={highlightedDocumentBlockId}
                          highlightedExcerpt={highlightedExcerpt}
                          highlightedExcerptStart={highlightedExcerptStart}
                          highlightedExcerptEnd={highlightedExcerptEnd}
                          language={language}
                          pdfPreviewUrl={video.document_preview_url}
                          testIdPrefix="desktop-document-viewer"
                        />
                      ) : (
                        <div className="space-y-4">
                          {isManualSpeakerEditorEnabled ? (
                            <div className="sticky top-0 z-20 border-b border-border/60 bg-card/95 px-4 pt-4 backdrop-blur lg:px-6 lg:pt-6">
                              <SpeakerTimelineEditor
                                segments={displaySegments || []}
                                profiles={speakerProfiles}
                                language={language}
                                loading={updatingSegmentSpeaker}
                                onAssignRange={handleUpdateSpeakerRange}
                                onDeleteSpeaker={handleDeleteSpeaker}
                                onIdentifySpeakers={handleIdentifySpeakers}
                                deletingSpeakerId={deletingSpeakerId}
                                identifyingSpeakers={isSpeakerAnalysisRunning}
                                identifyingSpeakersProgress={speakerAnalysisProgress}
                                identifyingSpeakersMessage={speakerAnalysisMessage}
                                onSeek={(seconds) => handleMomentClick({ time_seconds: seconds })}
                              />
                            </div>
                          ) : null}
                          <TranscriptViewer
                            ref={transcriptRef}
                            transcript={displayTranscript}
                            segments={sanitizedDisplaySegments}
                            highlightedSegment={highlightedSegment}
                            highlightedExcerpt={highlightedExcerpt}
                            isDocument={isDocument}
                            speakerProfiles={visibleSpeakerProfiles}
                            language={language}
                            onEditSpeakerSegment={!isDocument && isSpeakerFeatureEnabled ? (index, segment) => {
                              setSelectedSpeakerSegment({ index, segment });
                              setSegmentSpeakerDialogOpen(true);
                            } : undefined}
                            onEditTranscriptSegment={isTranscriptEditable ? (index, segment) => {
                              setSelectedTranscriptSegment({ index, segment });
                              setSegmentTranscriptDialogOpen(true);
                            } : undefined}
                          />
                        </div>
                      )
                    )}
                  </div>
                </Card>

                <Card className="bg-card border-border overflow-hidden" data-testid="desktop-summary-below-transcript-card">
                  <div className="p-4 border-b border-border flex items-center justify-between gap-3">
                    <div>
                      <h3 className="flex items-center gap-2 font-medium text-base lg:text-lg">
                        <FileText className="w-4 h-4 text-primary" />
                        {language === "es" ? "Resumen IA" : "AI Summary"}
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-muted-foreground lg:text-[0.95rem]">
                        {language === "es"
                          ? "Resumen estructurado del contenido"
                          : "Structured summary of the content"}
                      </p>
                    </div>
                    {!summary && (
                      <Button
                        onClick={handleSummarize}
                        disabled={summarizing}
                        size="sm"
                        variant="outline"
                        data-testid="summarize-btn"
                        translate="no"
                      >
                        {summarizing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            {language === "es" ? "Generar" : "Generate"}
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                  <div className="overflow-y-auto p-5 lg:p-6 min-h-[260px] max-h-[420px] xl:max-h-[520px]">
                    {summary ? (
                      <div className="prose dark:prose-invert max-w-none">
                        <div className="whitespace-pre-wrap text-base leading-8 lg:text-[1.08rem] lg:leading-9 xl:text-[1.12rem]">
                          {summary.split('\n').map((line, idx) => {
                            if (line.startsWith('**') && line.endsWith('**')) {
                              return <h4 key={idx} className="mt-5 mb-3 text-lg font-bold leading-8 text-foreground lg:text-[1.28rem] lg:leading-9">{line.replace(/\*\*/g, '')}</h4>;
                            }
                            if (line.startsWith('- ') || line.startsWith('• ')) {
                              return <li key={idx} className="mb-2 ml-5 text-base leading-8 text-muted-foreground lg:text-[1.08rem] lg:leading-9 xl:text-[1.12rem]">{line.substring(2)}</li>;
                            }
                            if (line.trim() === '') return <br key={idx} />;
                            return <p key={idx} className="mb-3 text-base leading-8 text-muted-foreground lg:text-[1.08rem] lg:leading-9 xl:text-[1.12rem]">{line}</p>;
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-10 text-muted-foreground">
                        <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                        <p className="text-sm">
                          {language === "es"
                            ? 'Haz clic en "Generar" para crear un resumen con IA'
                            : 'Click "Generate" to create an AI summary'}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>

                <TranscriptChat
                  videoId={videoId}
                  isEnabled={Boolean(user)}
                  membership={user?.membership || "free"}
                  contentKind={video.content_kind || "media"}
                  onDocumentCitationClick={handleDocumentCitationClick}
                  onTimestampClick={handleChatTimestampClick}
                  testIdPrefix="desktop-chat"
                  className="mx-auto mt-8 w-full max-w-[1120px] 2xl:mt-10 2xl:max-w-[1240px]"
                />
                <VideoReviewCard
                  videoId={videoId}
                  videoStatus={video?.status}
                  membership={user?.membership || "free"}
                  isAdmin={user?.is_admin}
                  language={language}
                />
              </div>
            </div>

            {/* Mobile Layout - Tabbed Interface */}
            <div className="lg:hidden">
              <Tabs defaultValue="keymoments" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4 notranslate" translate="no">
                  <TabsTrigger value="keymoments" className="flex items-center gap-1 text-xs px-2" data-testid="mobile-tab-keymoments" translate="no">
                    <Sparkles className="w-3 h-3" />
                    <span className="hidden xs:inline">{language === "es" ? "Momentos" : "Moments"}</span>
                    <span className="xs:hidden">{video.key_moments?.length || 0}</span>
                  </TabsTrigger>
                  <TabsTrigger value="transcript" className="flex items-center gap-1 text-xs px-2" data-testid="mobile-tab-transcript" translate="no">
                    <MessageSquareText className="w-3 h-3" />
                    <span>{language === "es" ? "Transcripción" : "Transcript"}</span>
                  </TabsTrigger>
                  <TabsTrigger value="summary" className="flex items-center gap-1 text-xs px-2" data-testid="mobile-tab-summary" translate="no">
                    <Sparkles className="w-3 h-3" />
                    <span>{language === "es" ? "Resumen" : "Summary"}</span>
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="keymoments" className="mt-0">
                  <Card className="bg-card border-border">
                    <div className="p-3 border-b border-border">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="font-medium text-sm flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-primary" />
                            {language === "es" ? "Momentos Clave" : "Key Moments"} ({video.key_moments?.length || 0})
                          </h3>
                          <p className="text-xs text-muted-foreground mt-1">
                            {language === "es" 
                              ? (isPdfDocument ? "Toca cualquier punto para ir a la página del PDF donde aparece ese texto" : (isDocument ? "Toca cualquier punto para ir a ese fragmento del texto" : "Toca cualquier momento para ir a esa parte"))
                              : (isPdfDocument ? "Tap any point to jump to the PDF page where that text appears" : (isDocument ? "Tap any point to jump to that text fragment" : "Tap any moment to jump to that part"))}
                          </p>
                        </div>
                      {showSpeakerControls ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setSpeakerDialogOpen(true)}
                            className="speaker-edit-cta h-8 px-3 text-xs"
                            data-testid="mobile-speaker-action-button"
                          >
                            <Mic2 className="mr-2 h-3.5 w-3.5" />
                            {speakerButtonLabel}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                    <div className="max-h-[68vh] overflow-auto">
                      {translatingMoments ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="text-center">
                            <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">
                              {language === "es" ? "Traduciendo momentos..." : "Translating moments..."}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <KeyMomentsList 
                          moments={sanitizedKeyMoments} 
                          onMomentClick={(moment) => {
                            handleMomentClick(moment);
                            const transcriptTab = document.querySelector('[data-testid="mobile-tab-transcript"]');
                            if (transcriptTab) transcriptTab.click();
                          }}
                          isDocument={isDocument}
                          speakerInsights={speakerInsights}
                          speakerProfiles={visibleSpeakerProfiles}
                          language={language}
                        />
                      )}
                    </div>
                  </Card>
                </TabsContent>
                
                <TabsContent value="transcript" className="mt-0">
                  <Card className="bg-card border-border">
                    <div className="p-3 border-b border-border">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-medium text-sm flex items-center gap-2">
                            <MessageSquareText className="w-4 h-4 text-primary" />
                            {isDocument
                              ? (hasDocumentPdfPreview ? (language === "es" ? "PDF original" : "Original PDF") : (language === "es" ? "Documento de texto" : "Text document"))
                              : (language === "es" ? "Transcripción Completa" : "Full Transcript")}
                          </h3>
                          <p className="text-xs text-muted-foreground mt-1">
                            {isDocument
                              ? (hasDocumentPdfPreview
                                ? (language === "es" ? "Vista fiel del PDF y salto directo por páginas" : "Accurate PDF preview with direct page jumps")
                                : (language === "es" ? "Texto ordenado del documento" : "Structured document text"))
                              : `${displaySegments?.length || video.segments?.length || 0} ${language === "es" ? "fragmentos" : "fragments"}`}
                          </p>
                        </div>
                        {isTranscriptEditable ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setBulkTranscriptDialogOpen(true)}
                            className="h-8 px-3 text-xs"
                            data-testid="mobile-transcript-tab-edit-button"
                          >
                            <PencilLine className="mr-2 h-3.5 w-3.5" />
                            {language === "es" ? "Editar" : "Edit"}
                          </Button>
                        ) : null}
                      </div>
                      {!isDocument ? (
                        <div
                          className="mx-3 mb-3 rounded-2xl border border-primary/20 bg-primary/5 px-3 py-3 text-xs leading-6 text-muted-foreground"
                          data-testid="mobile-transcript-accuracy-notice"
                        >
                          {transcriptAccuracyNotice}
                        </div>
                      ) : null}
                    </div>
                    <div className="max-h-[72vh] overflow-auto">
                      {translatingTranscript ? (
                        <div className="flex items-center justify-center py-12" data-testid="mobile-transcript-translation-loading">
                          <div className="text-center">
                            <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">
                              {language === "es" ? "Traduciendo transcripción..." : "Translating transcript..."}
                            </p>
                          </div>
                        </div>
                      ) : (
                        isDocument && displayDocumentBlocks.length > 0 ? (
                          <DocumentViewer
                            ref={transcriptRef}
                            documentBlocks={displayDocumentBlocks}
                            highlightedBlockId={highlightedDocumentBlockId}
                            highlightedExcerpt={highlightedExcerpt}
                            highlightedExcerptStart={highlightedExcerptStart}
                            highlightedExcerptEnd={highlightedExcerptEnd}
                            language={language}
                            pdfPreviewUrl={video.document_preview_url}
                            testIdPrefix="mobile-document-viewer"
                          />
                        ) : (
                          <div className="space-y-4">
                          {isManualSpeakerEditorEnabled ? (
                              <div className="sticky top-0 z-20 border-b border-border/60 bg-card/95 px-3 pt-3 backdrop-blur">
                                <SpeakerTimelineEditor
                                  segments={displaySegments || []}
                                  profiles={speakerProfiles}
                                  language={language}
                                  loading={updatingSegmentSpeaker}
                                  onAssignRange={handleUpdateSpeakerRange}
                                  onDeleteSpeaker={handleDeleteSpeaker}
                                  onIdentifySpeakers={handleIdentifySpeakers}
                                  deletingSpeakerId={deletingSpeakerId}
                                  identifyingSpeakers={isSpeakerAnalysisRunning}
                                identifyingSpeakersProgress={speakerAnalysisProgress}
                                identifyingSpeakersMessage={speakerAnalysisMessage}
                                  onSeek={(seconds) => handleMomentClick({ time_seconds: seconds })}
                                />
                              </div>
                            ) : null}
                            <TranscriptViewer
                              ref={transcriptRef}
                              transcript={displayTranscript}
                              segments={sanitizedDisplaySegments}
                              highlightedSegment={highlightedSegment}
                              highlightedExcerpt={highlightedExcerpt}
                              isDocument={isDocument}
                              speakerProfiles={visibleSpeakerProfiles}
                              language={language}
                              onEditSpeakerSegment={!isDocument && isSpeakerFeatureEnabled ? (index, segment) => {
                                setSelectedSpeakerSegment({ index, segment });
                                setSegmentSpeakerDialogOpen(true);
                              } : undefined}
                              onEditTranscriptSegment={isTranscriptEditable ? (index, segment) => {
                                setSelectedTranscriptSegment({ index, segment });
                                setSegmentTranscriptDialogOpen(true);
                              } : undefined}
                            />
                          </div>
                        )
                      )}
                    </div>
                  </Card>
                </TabsContent>

                <TabsContent value="summary" className="mt-0">
                  <Card className="bg-card border-border">
                    <div className="p-3 border-b border-border flex items-center justify-between">
                      <h3 className="font-medium text-sm flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        {language === "es" ? "Resumen IA" : "AI Summary"}
                      </h3>
                      {!summary && (
                        <Button
                          onClick={handleSummarize}
                          disabled={summarizing}
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          data-testid="mobile-summarize-btn"
                          translate="no"
                        >
                          {summarizing ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Sparkles className="w-3 h-3 mr-1" />
                              {language === "es" ? "Generar" : "Generate"}
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    <div className="max-h-[72vh] overflow-auto p-4">
                      {summary ? (
                        <div className="prose dark:prose-invert max-w-none">
                          <div className="whitespace-pre-wrap text-base leading-8">
                            {summary.split('\n').map((line, idx) => {
                              if (line.startsWith('**') && line.endsWith('**')) {
                                return <h4 key={idx} className="mt-4 mb-3 text-base font-bold leading-8 text-foreground">{line.replace(/\*\*/g, '')}</h4>;
                              }
                              if (line.startsWith('- ') || line.startsWith('• ')) {
                                return <li key={idx} className="mb-2 ml-5 text-base leading-8 text-muted-foreground">{line.substring(2)}</li>;
                              }
                              if (line.trim() === '') return <br key={idx} />;
                              return <p key={idx} className="mb-3 text-base leading-8 text-muted-foreground">{line}</p>;
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                          <p className="text-sm">
                            {language === "es" 
                              ? 'Toca "Generar" para crear un resumen con IA'
                              : 'Tap "Generate" to create an AI summary'}
                          </p>
                        </div>
                      )}
                    </div>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>

            {video && video.status === "completed" && (
              <div className="lg:hidden mt-8">
                <TranscriptChat 
                  videoId={videoId}
                  isEnabled={Boolean(user)}
                  membership={user?.membership || "free"}
                  contentKind={video.content_kind || "media"}
                  onDocumentCitationClick={handleDocumentCitationClick}
                  onTimestampClick={handleChatTimestampClick}
                  testIdPrefix="mobile-chat"
                />
                <VideoReviewCard
                  videoId={videoId}
                  videoStatus={video?.status}
                  membership={user?.membership || "free"}
                  isAdmin={user?.is_admin}
                  language={language}
                />
              </div>
            )}
          </>
        )}
      </main>

      {isSpeakerFeatureEnabled ? (
        <>
          <SpeakerManagementDialog
            open={speakerDialogOpen}
            onOpenChange={setSpeakerDialogOpen}
            profiles={speakerProfiles}
            language={language}
            onSaveSpeaker={handleRenameSpeaker}
            onMergeSpeaker={handleMergeSpeakers}
            onDeleteSpeaker={handleDeleteSpeaker}
            savingSpeakerId={savingSpeakerId}
            mergingSpeakerId={mergingSpeakerId}
            deletingSpeakerId={deletingSpeakerId}
          />

          <SegmentSpeakerDialog
            open={segmentSpeakerDialogOpen}
            onOpenChange={setSegmentSpeakerDialogOpen}
            segment={selectedSpeakerSegment?.segment}
            segmentIndex={selectedSpeakerSegment?.index}
            speakers={speakerProfiles}
            language={language}
            onSave={handleUpdateSegmentSpeaker}
            loading={updatingSegmentSpeaker}
          />
        </>
      ) : null}

      {isTranscriptEditable ? (
        <>
          <TranscriptBulkEditorDialog
            open={bulkTranscriptDialogOpen}
            onOpenChange={setBulkTranscriptDialogOpen}
            transcript={editableTranscriptText}
            language={language}
            loading={savingTranscript}
            onSave={handleSaveBulkTranscript}
          />

          <TranscriptSegmentEditorDialog
            open={segmentTranscriptDialogOpen}
            onOpenChange={setSegmentTranscriptDialogOpen}
            segment={selectedTranscriptSegment?.segment}
            segmentIndex={selectedTranscriptSegment?.index ?? 0}
            language={language}
            loading={savingTranscript}
            onSave={handleSaveTranscriptSegment}
          />
        </>
      ) : null}
    </div>
  );
};

export default VideoDetailPage;
                 